import { type ChannelMeterRow, useChannelsFlat, useObjects } from "@features/devices/api";
import { type DirectoryGroupChannel, useGroups } from "@features/groups/api";
import { useNetworkTelemetry } from "@features/reports/api";
import { buildExportUrl } from "@shared/buildExportUrl";
import { useGlobalObject } from "@shared/hooks/useGlobalObject";
import { useEffect, useMemo, useRef } from "react";

import type { NetworkTelemetryScope } from "./store";
import { useNetworkTelemetryStore } from "./store";

export const useNetworkTelemetryReport = () => {
  const store = useNetworkTelemetryStore();
  const {
    scope,
    interval,
    selectedChannel,
    selectedGroup,
    selectedGroupChannel,
    period,
    setSelectedChannel,
    setSelectedGroup,
    setSelectedGroupChannel,
    resetChannels,
  } = store;

  const objectsQuery = useObjects();
  const objects = objectsQuery.data ?? [];
  const { selectedObject } = useGlobalObject(objects);

  // Cascade reset channels/groups when object changes (including cross-page navigation)
  const prevObjRef = useRef<typeof selectedObject>(undefined);
  useEffect(() => {
    if (
      selectedObject &&
      (!prevObjRef.current ||
        prevObjRef.current.objectId !== selectedObject.objectId ||
        prevObjRef.current.regionId !== selectedObject.regionId)
    ) {
      resetChannels();
    }
    prevObjRef.current = selectedObject;
  }, [selectedObject, resetChannels]);

  const regionID = selectedObject?.regionId;
  const objectID = selectedObject?.objectId;

  const channelsQuery = useChannelsFlat(objectID);
  const channelOptions = useMemo(
    () =>
      (channelsQuery.data ?? []).map((channel: ChannelMeterRow) => ({
        label: `${channel.meter_number} — ${channel.feeder_name ?? channel.channel_label ?? "канал"}`,
        value: channel.meter_number,
      })),
    [channelsQuery.data],
  );

  // Auto-select first channel
  useEffect(() => {
    if (scope !== "channel") return;
    if (selectedChannel && channelOptions.some((opt) => opt.value === selectedChannel)) return;
    setSelectedChannel(channelOptions[0]?.value);
  }, [channelOptions, scope, selectedChannel, setSelectedChannel]);

  const groupsQuery = useGroups(
    selectedObject
      ? { object: { regionId: selectedObject.regionId, objectId: selectedObject.objectId } }
      : undefined,
  );
  const groupOptions = useMemo(
    () =>
      (groupsQuery.data ?? []).map((g) => ({
        label: `${g.name} (${g.channels?.length ?? 0} каналов)`,
        value: g.id,
      })),
    [groupsQuery.data],
  );

  const selectedGroupData = useMemo(() => {
    if (!selectedGroup) return null;
    return (groupsQuery.data ?? []).find((g) => g.id === selectedGroup) ?? null;
  }, [groupsQuery.data, selectedGroup]);

  const groupChannels: DirectoryGroupChannel[] = selectedGroupData?.channels ?? [];

  const groupChannelOptions = useMemo(() => {
    if (!groupChannels.length) return [];
    const channels = channelsQuery.data ?? [];
    return groupChannels.map((ch) => {
      const channelInfo = channels.find((c) => c.meter_number === ch.channel_id);
      const label = channelInfo
        ? `${ch.channel_id} — ${channelInfo.feeder_name ?? channelInfo.channel_label ?? "канал"}`
        : `Канал ${ch.channel_id}`;
      return { label, value: ch.channel_id };
    });
  }, [groupChannels, channelsQuery.data]);

  // Auto-select first group
  useEffect(() => {
    if (scope !== "group") return;
    if (selectedGroup && groupOptions.some((opt) => opt.value === selectedGroup)) return;
    setSelectedGroup(groupOptions[0]?.value);
    setSelectedGroupChannel(null);
  }, [groupOptions, scope, selectedGroup, setSelectedGroup, setSelectedGroupChannel]);

  const effectiveScope: NetworkTelemetryScope =
    scope === "group" && selectedGroupChannel ? "channel" : scope;
  const effectiveChannelID =
    scope === "group" && selectedGroupChannel ? selectedGroupChannel : selectedChannel;

  const fromISO = period.from.toDate().toISOString();
  const toISO = period.to.toDate().toISOString();

  const ready =
    Boolean(fromISO && toISO) &&
    (scope === "group"
      ? selectedGroupChannel
        ? Boolean(regionID !== undefined && objectID !== undefined)
        : Boolean(selectedGroup)
      : scope === "channel"
        ? Boolean(regionID !== undefined && objectID !== undefined && selectedChannel !== undefined)
        : Boolean(regionID !== undefined && objectID !== undefined));

  const telemetryQuery = useNetworkTelemetry({
    scope: effectiveScope,
    region: regionID,
    object: objectID,
    channel: effectiveScope === "channel" ? effectiveChannelID : undefined,
    groupId: effectiveScope === "group" ? selectedGroup : undefined,
    from: fromISO,
    to: toISO,
    interval,
  });

  const rows = telemetryQuery.data?.rows ?? [];

  const exportUrl = ready
    ? buildExportUrl("/api/reports/networktelemetry", {
        scope: effectiveScope,
        region: regionID,
        object: objectID,
        channel: effectiveScope === "channel" ? effectiveChannelID : undefined,
        group_id: effectiveScope === "group" ? selectedGroup : undefined,
        from: fromISO,
        to: toISO,
        interval,
      })
    : undefined;

  const currentObject = useMemo(
    () => objects.find((obj) => obj.n_ob === objectID && obj.syb_rnk === regionID),
    [objects, objectID, regionID],
  );

  return {
    objectsQuery,
    objects,
    channelOptions,
    channelsQuery,
    groupOptions,
    groupsQuery,
    groupChannelOptions,
    selectedGroupData,
    ready,
    telemetryQuery,
    rows,
    exportUrl,
    currentObject,
    fromISO,
    toISO,
  };
};
